
import { Vue, Options } from "vue-class-component";
import Multiselect from "@/app/ui/components/multiselect-search-tag/index.vue";
import MultipleSelectV3 from "@/app/ui/components/multiple-select-v3/index.vue";
import { ListSttController } from "@/app/ui/controllers/ListSttController";
import { ShipmentBookingController } from "@/app/ui/controllers/ShipmentBookingController";
import { BookingController } from "@/app/ui/controllers/BookingController";
import { PrintReceiptController } from "@/app/ui/controllers/PrintReceiptController";
import { AddDraftReceiptApiRequest } from "@/data/payload/api/AddDraftReceiptApiRequest";
import { DetailClientManagement } from "@/domain/entities/ClientManagement";
import { formatDateNormal } from "@/app/infrastructures/misc/Utils";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ErrorMessageEntities } from "@/domain/entities/MainApp";
import FormInvoiceDetail from "../components/FormInvoiceDetail.vue";
import { GetPreviewData, InvoiceForUpdate } from "@/domain/entities/PrintReceipt";
import CustomDropdown from "@/app/ui/components/custom-dropdown/index.vue";
import { FAILED_ERROR, parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import Checkbox from "primevue/checkbox";
import { DraftPreviewForUpdateRequest } from "@/data/payload/api/PrintReceiptApiRequest";
import { debounce } from "lodash";
import { PartnerController } from "@/app/ui/controllers/PartnerController";

interface SttItem {
  name: string;
  id?: string | number;
  isError?: boolean;
}

@Options({
  components: {
    Multiselect,
    MultipleSelectV3,
    FormInvoiceDetail,
    CustomDropdown,
    Checkbox
  }
})
export default class CreateReceipt extends Vue {
  sttListValue: SttItem[] = [];

  startDate: Date | null = null;
  endDate: Date | null = null;
  selectedCustomer: any = { name: "", id: 0 };
  sttType: string[] = [];
  serviceType: string[] = [];
  customerType: { name: string, value: string } = { name: "", value: "" };
  customerName = "";

  customerList: Array<any> = [];
  isFormDetail = false;
  detailData = new InvoiceForUpdate();
  detailPreviewData: Array<any> = [];

  get routerId() {
    return this.$route.params.id;
  }

  async mounted() {
    if (!this.routerId) await PrintReceiptController.clearDraftItems();
    this.getRefTypeList();
    ShipmentBookingController.searchAction("");
    this.refType = this.refTypeList[0]
    if (this.routerId) {
      this.fetchInvoiceForUpdate(Number(this.routerId));
    }
  }

  goBack() {
    this.$router.push("/shipment/receipt-v2");
  }

  async fetchInvoiceForUpdate(id: number) {
    try {
      MainAppController.showLoading();
      const res = await PrintReceiptController.getInvoiceForUpdate(id);
      this.detailData = res;
      this.detailPreviewData = this.detailData.items;
      this.isFormDetail = true;
    } catch (err) {
      MainAppController.showErrorMessage(
        new ErrorMessageEntities({
          type: FAILED_ERROR,
          title: this.$t("tagihan.errorGetDetail"),
          message: err.response ? err.response?.data.message.id : "",
          onClose: this.goBack()
        })
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  async draftPreviewForUpdate(id: number) {
    try {
      MainAppController.showLoading();
      const sttNameList: string[] = this.sttListValue.map((item) => item.name);
      const mCustomer = this.selectedCustomer;
      const payload = new DraftPreviewForUpdateRequest(
        mCustomer.id,
        this.refType.value,
        mCustomer.customerName,
        mCustomer.customerPhoneNumber,
        formatDateNormal(this.startDate, "YYYY-MM-DD"),
        formatDateNormal(this.endDate, "YYYY-MM-DD"),
        this.sttType,
        this.serviceType,
        sttNameList
      );
      const res = await PrintReceiptController.draftPreviewForUpdate({id, payload})
      const existingSttNos = new Set(
        this.detailData.items.map((item) => item.sttNo)
      );
      const filteredData = res.items.filter(
        (item) => !existingSttNos.has(item.sttNo)
      );
      this.detailData.items = [...filteredData, ...this.detailData.items];
      this.detailPreviewData = this.detailData.items;
      this.isFormDetail = true;
      this.onResetState();
    } catch (err) {
      this.isFormDetail = false;
      MainAppController.showErrorMessage(
        parsingErrorResponse(err, "Gagal Menambah Draft!", () => {
          this.draftPreviewForUpdate(id);
          MainAppController.closeErrorMessage();
        })
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  get isValid() {
    return !!(
      this.refType.value &&
      this.selectedCustomer.id &&
      this.startDate && this.endDate &&
      this.sttType.length &&
      this.serviceType.length
    );
  }

  get isError(): boolean {
    return ListSttController.isError;
  }

  get errorMessageLength(): number {
    const error = ListSttController.errorMessageList;
    if (!error) return 0;
    return error.split(",").length;
  }

  get sttManualList(): SttItem[] {
    const data = ShipmentBookingController.shipmentBookingData?.data || [];

    return data.map((e: any) => ({
      name: e.sttNo,
      id: e.sttId,
      isError: false
    }));
  }

  onSearch(e: Event): void {
    const target = e.target as HTMLInputElement;
    ShipmentBookingController.searchAction(target?.value || "");
  }

  onGenerate(): void {
    this.resetResiListData();
    if (this.routerId) {
      this.draftPreviewForUpdate(Number(this.routerId));
    } else {
      this.addDraft();
    }
    ListSttController.resetErrorMessage("");
  }

  detailClientManagement = new DetailClientManagement();
  async addDraft() {
    MainAppController.showLoading();
    try {
      const mCustomer = this.selectedCustomer;
      const sttNameList: string[] = this.sttListValue.map((item) => item.name);
      const payload = new AddDraftReceiptApiRequest(
        mCustomer.id,
        this.refType.value,
        mCustomer.customerName,
        mCustomer.customerPhoneNumber,
        formatDateNormal(this.startDate, "YYYY-MM-DD"),
        formatDateNormal(this.endDate, "YYYY-MM-DD"),
        this.sttType,
        this.serviceType,
        sttNameList
      );
      await PrintReceiptController.addDraft(payload);
      await this.getPreview();
    } catch (error) {
      this.isFormDetail = false;
      MainAppController.showErrorMessage(
        new ErrorMessageEntities({
          title: "Update Gagal",
          message: (error as any)?.message || "Gagal update status lunas",
          onClose: () => MainAppController.closeErrorMessage()
        })
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  async getPreview() {
    MainAppController.showLoading();
    try {
      const resp = await PrintReceiptController.getPreview();
      this.detailPreviewData = Array.isArray(resp.data) ? resp.data.flat() : [];

      this.isFormDetail = true;
      this.onResetState();
    } catch (error) {
      MainAppController.showErrorMessage(
      parsingErrorResponse(error, "Gagal mendapatkan data!", () => {
        MainAppController.closeErrorMessage();
      }));
    } finally {
      MainAppController.closeLoading();
    }
  }

  resetResiListData(): void {
    ListSttController.resetResiListData();
  }

  clearSttListValue(): void {
    if (this.sttListValue.length === 0) {
      ListSttController.resetSttList();
      ListSttController.setShowTable(false);
    }
  }

  setPeriode(value: Date[]): void {
    this.startDate = value[0] ?? null;
    this.endDate = value[1] ?? null;
  }

  get setErrorList(): string {
    const errorString = ListSttController.errorMessageList;
    if (!errorString) return "";

    const errorList = errorString.split(",");

    this.sttListValue = this.sttListValue.map((item) => ({
      ...item,
      isError: errorList.includes(item.name)
    }));

    return "";
  }

  get getDataCustomerList() {
    return BookingController.customerListData
  }

  get getDataClientList() {
    return this.mapName(PartnerController.clientDataNamePOS.data)
  }

  async fetchCustomerList() {
    await BookingController.getCustomerList({
      search: this.customerName,
      cache: true,
      refId: 0,
      refType: this.refType.value
    });
  }

  async fetchClientList() {
    await PartnerController.getClientNamePOS({
      search: this.customerName,
      page: 1,
      limit: 10
    });
  }

  mapName(response: any) {
    return response.map((e: any) => ({
      customerName: e.clientName,
      id: e.clientId,
      name: `${e.clientName}`
    }))
  }

  onChangeCustomerName = debounce((keyword: string) => {
   this.customerName = keyword;

    this.fetchCustomerList();
  }, 500);

  onChangeClientName = debounce((keyword: string) => {
   this.customerName = keyword;

    this.fetchClientList();
  }, 500);

  onCloseSearchCustomerList() {
    if(this.selectedCustomer.id) return

    this.customerName = "";
  }

  get isLoadingCustomerList() {
    return BookingController.isLoadingCustomer;
  }

  get isLaodingClientList() {
    return PartnerController.loadingClientsNamePOS
  }

  refType = { name: "Pengirim", value: "sender" };
  refTypeList: Array<any> = [];
  getRefTypeList() {
    const mList = [
      { name: this.$t("tagihan.sender"), value: "sender" },
      { name: this.$t("tagihan.client"), value: "client" }
    ];
    this.refTypeList = mList;
  }

  onChangeRefType(refType: {name: string, value: string}) {
    this.refType = refType;

    this.customerList = []
    this.selectedCustomer = {name: "", id: 0}
    this.customerName = "";
  }

  get dataInvoiceDetail() {
    if (this.routerId) {
      return this.detailData.items;
    } else {
      return PrintReceiptController.getPreviewSuccessData.data.forEach((item: GetPreviewData) => {
          return {
            id: item.id,
            sttNo: item.sttNo,
            shipmentId: item.shipmentId,
            isCod: item.isCod,
            isDfod: item.isDfod,
            isCashless: item.isCashless,
            createdAt: item.createdAt,
            recipientName: item.recipientName,
            recipientPhoneMasked: item.recipientPhoneMasked,
            destination3lc: item.destination3lc,
            destinationName: item.destinationName,
            productType: item.productType,
            chargeableWeight: item.chargeableWeight,
            tariff: item.tariff,
            amount: item.amount,
            paymentLabel: item.paymentLabel,
            senderName: item.senderName,
            senderPhone: item.senderPhone
          };
        }
      );
    }
  }

  get tipeLayananOptions() {
    return [
      {
        name: "Non-COD",
        value: "non_cod"
      },
      {
        name: "COD",
        value: "cod"
      },
      {
        name: "DFOD",
        value: "dfod"
      }
    ]
  }

  get tipeSttOptions() {
    return [
      {
        name: "Retail",
        value: "retail"
      },
      {
        name: "CBP",
        value: "cbp"
      },
      {
        name: "Customer App",
        value: "customer_app"
      }
    ]
  }

  onResetState() {
    this.selectedCustomer = { name: "", id: 0 },
    (this.$refs.rangePicker as any)?.reset();
    this.sttType = [];
    this.serviceType = [];
    this.sttListValue = [];
  }
}
